import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { MdDelete, MdFileDownload, MdPeople, MdPersonAdd, MdVisibility } from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Button, { IconButton } from "components/button";
import Badge from "components/badge";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import DataTable from "components/data-table";
import { SearchInput } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import { formatDate, formatDateTime, EMPTY } from "utils/format";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

const SYNC_BADGES = {
  synced: { tone: "success", label: "Added" },
  no_email: { tone: "neutral", label: "No Email" },
  failed: { tone: "danger", label: "Add Failed" },
};
const PENDING_BADGE = { tone: "neutral", label: "Not Added" };

const fullName = (visitor) =>
  [visitor.firstName, visitor.lastName].filter(Boolean).join(" ") || visitor.email || "Visitor";

const canAddToPeople = (visitor) =>
  visitor.syncStatus !== "synced" && visitor.syncStatus !== "no_email";

const matchesQuery = (visitor, query) =>
  visitor.firstName?.toLowerCase().includes(query) ||
  visitor.lastName?.toLowerCase().includes(query) ||
  visitor.email?.toLowerCase().includes(query) ||
  visitor.phone?.includes(query);

const visitorsUrl = (limit, offset) => `/connect/visitors?limit=${limit}&offset=${offset}`;

// Loads every submission; retries once with the server total if the known total was stale.
const fetchAllVisitors = async (knownTotal) => {
  const response = await AtriumApi.get(visitorsUrl(Math.max(knownTotal, 1), 0));
  const { data = [], total = 0 } = response.data;
  if (total <= data.length) return response.data;
  return (await AtriumApi.get(visitorsUrl(total, 0))).data;
};

const SyncBadge = ({ status }) => {
  const { tone, label } = SYNC_BADGES[status] || PENDING_BADGE;
  return <Badge tone={tone}>{label}</Badge>;
};

const DetailItem = ({ label, children }) => (
  <div>
    <dt className="text-sm font-medium text-fg-muted">{label}</dt>
    <dd className="mt-1 text-fg">{children}</dd>
  </div>
);

const VisitorDetails = ({ visitor }) => (
  <dl className="flex flex-col gap-4">
    <DetailItem label="Name">{fullName(visitor)}</DetailItem>
    <DetailItem label="Email">
      {visitor.email ? (
        <a href={`mailto:${visitor.email}`} className="underline underline-offset-2 hover:no-underline">
          {visitor.email}
        </a>
      ) : (
        EMPTY
      )}
    </DetailItem>
    {visitor.phone && (
      <DetailItem label="Phone">
        <a href={`tel:${visitor.phone}`} className="underline underline-offset-2 hover:no-underline">
          {visitor.phone}
        </a>
      </DetailItem>
    )}
    {visitor.message && (
      <DetailItem label="Message">
        <p className="whitespace-pre-wrap rounded-control bg-surface-subtle p-3 text-sm">
          {visitor.message}
        </p>
      </DetailItem>
    )}
    <DetailItem label="People">
      <SyncBadge status={visitor.syncStatus} />
    </DetailItem>
    <DetailItem label="Submitted">{formatDateTime(visitor.createdAt)}</DetailItem>
  </dl>
);

const downloadCsv = (visitors) => {
  const headers = ["First Name", "Last Name", "Email", "Phone", "Message", "Submitted Date"];
  const rows = visitors.map((visitor) => [
    visitor.firstName || "",
    visitor.lastName || "",
    visitor.email || "",
    visitor.phone || "",
    visitor.message || "",
    formatDate(visitor.createdAt),
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `visitor-submissions-${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const VisitorSubmissions = () => {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const requestRef = useRef(0);
  const totalRef = useRef(0);

  // Search runs client-side over every submission; otherwise the server pages.
  const searching = searchQuery.trim() !== "";
  const fetchLimit = searching ? null : pageSize;
  const fetchOffset = searching ? 0 : pageIndex * pageSize;

  const loadVisitors = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestRef.current;
      if (!silent) setLoading(true);
      setLoadError(null);
      try {
        const body = fetchLimit
          ? (await AtriumApi.get(visitorsUrl(fetchLimit, fetchOffset))).data
          : await fetchAllVisitors(totalRef.current);
        if (requestId !== requestRef.current) return;
        totalRef.current = body.total || 0;
        setRows(body.data || []);
        setTotal(body.total || 0);
      } catch (error) {
        if (requestId === requestRef.current) setLoadError(error);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [fetchLimit, fetchOffset]
  );

  useEffect(() => {
    loadVisitors();
  }, [loadVisitors]);

  const filteredRows = useMemo(() => {
    if (!searching) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((visitor) => matchesQuery(visitor, query));
  }, [rows, searching, searchQuery]);

  const pageRows = useMemo(
    () =>
      searching
        ? filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
        : filteredRows,
    [filteredRows, searching, pageIndex, pageSize]
  );

  const refreshAfterRemoval = useCallback(() => {
    if (pageRows.length === 1 && pageIndex > 0) {
      setPageIndex(pageIndex - 1);
      // Stepping back a server page refetches through the effect.
      if (!searching) return;
    }
    loadVisitors({ silent: true });
  }, [pageRows.length, pageIndex, searching, loadVisitors]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPageIndex(0);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageIndex(0);
  };

  const closeDetails = () => setSelectedVisitor(null);

  const handleDeleteVisitor = useCallback(
    async (visitor) => {
      const confirmed = await confirm({
        title: `Delete submission from "${fullName(visitor)}"?`,
        message: "This visitor submission will be permanently deleted.",
        confirmLabel: "Delete",
        tone: "danger",
      });
      if (!confirmed) return false;

      try {
        const response = await AtriumApi.delete(`/connect/visitors/${visitor.id}`);
        if (!response.data.success) return false;
        notify.success("Visitor submission deleted");
        refreshAfterRemoval();
        return true;
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not delete visitor submission"));
        return false;
      }
    },
    [confirm, refreshAfterRemoval]
  );

  const handleAddToPeople = useCallback(
    async (visitor) => {
      setSyncingId(visitor.id);
      try {
        const response = await AtriumApi.post(`/connect/visitors/${visitor.id}/sync`);
        if (response.data.success) {
          const markSynced = (v) => (v?.id === visitor.id ? { ...v, syncStatus: "synced" } : v);
          setRows((prev) => prev.map(markSynced));
          setSelectedVisitor(markSynced);
          notify.success("Added to People Management");
          loadVisitors({ silent: true });
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not add to People"));
      } finally {
        setSyncingId(null);
      }
    },
    [loadVisitors]
  );

  const handleDeleteSelected = async () => {
    if (await handleDeleteVisitor(selectedVisitor)) closeDetails();
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const response = await AtriumApi.get(visitorsUrl(total, 0));
      const query = searchQuery.toLowerCase();
      const visitors = (response.data.data || []).filter(
        (visitor) => !searching || matchesQuery(visitor, query)
      );
      if (visitors.length === 0) {
        notify.error("No data to export");
        return;
      }
      downloadCsv(visitors);
      notify.success("CSV exported");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not export visitor submissions"));
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => <span className="font-medium">{fullName(row.original)}</span>,
      },
      {
        id: "email",
        header: "Email",
        cell: ({ row }) => <span className="text-fg-muted">{row.original.email || EMPTY}</span>,
      },
      {
        id: "phone",
        header: "Phone",
        cell: ({ row }) => <span className="text-fg-muted">{row.original.phone || EMPTY}</span>,
      },
      {
        id: "submitted",
        header: "Submitted",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-fg-muted">{formatDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: "people",
        header: "People",
        cell: ({ row }) => <SyncBadge status={row.original.syncStatus} />,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const visitor = row.original;
          const name = fullName(visitor);
          return (
            <div className="flex justify-end gap-1">
              {canAddToPeople(visitor) && (
                <IconButton
                  icon={<MdPersonAdd />}
                  label={`Add “${name}” to People`}
                  onClick={() => handleAddToPeople(visitor)}
                  loading={syncingId === visitor.id}
                />
              )}
              <IconButton
                icon={<MdVisibility />}
                label={`View “${name}”`}
                onClick={() => setSelectedVisitor(visitor)}
              />
              <IconButton
                icon={<MdDelete />}
                label={`Delete “${name}”`}
                onClick={() => handleDeleteVisitor(visitor)}
              />
            </div>
          );
        },
      },
    ],
    [handleAddToPeople, handleDeleteVisitor, syncingId]
  );

  const renderContent = () => {
    if (loadError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load visitor submissions")}
            onRetry={() => loadVisitors()}
          />
        </Card>
      );
    }
    if (!loading && total === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdPeople}
            title="No Visitor Submissions Yet"
            description="Visitor contact form submissions will appear here."
          />
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by name, email, or phone…"
          ariaLabel="Search visitor submissions"
          className="w-full md:w-80"
        />
        <DataTable
          aria-label="Visitor submissions"
          columns={columns}
          data={pageRows}
          loading={loading}
          getRowId={(visitor) => String(visitor.id)}
          emptyState={
            <EmptyState
              variant="filtered"
              title="No Submissions Match Your Search"
              action={
                <Button variant="ghost" onClick={() => handleSearchChange("")}>
                  Clear Search
                </Button>
              }
            />
          }
          pagination={{
            pageIndex,
            pageSize,
            total: searching ? filteredRows.length : total,
            onPageChange: setPageIndex,
            onPageSizeChange: handlePageSizeChange,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
      </div>
    );
  };

  const selectedCanAdd = selectedVisitor && canAddToPeople(selectedVisitor);

  return (
    <PageContainer>
      <PageHeader
        title="Visitors"
        description="View and manage contact form submissions"
        actions={
          !loadError &&
          total > 0 && (
            <Button
              variant="secondary"
              leftIcon={<MdFileDownload />}
              onClick={exportToCSV}
              loading={exporting}
            >
              Export CSV
            </Button>
          )
        }
      />

      {renderContent()}

      <Dialog
        isOpen={Boolean(selectedVisitor)}
        onClose={closeDetails}
        title="Visitor Submission"
        footer={
          <>
            <Button
              variant="secondary"
              leftIcon={<MdDelete />}
              onClick={handleDeleteSelected}
              className="mr-auto"
            >
              Delete
            </Button>
            <Button variant="secondary" onClick={closeDetails}>
              Close
            </Button>
            {selectedCanAdd && (
              <Button
                leftIcon={<MdPersonAdd />}
                onClick={() => handleAddToPeople(selectedVisitor)}
                loading={syncingId === selectedVisitor.id}
              >
                Add to People
              </Button>
            )}
          </>
        }
      >
        {selectedVisitor && <VisitorDetails visitor={selectedVisitor} />}
      </Dialog>
    </PageContainer>
  );
};

export default VisitorSubmissions;
